import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TabsContainer from "@components/Tabs/TabsContainer";
import { useTheme } from "@hooks/useTheme";
import { useOnClickOutside } from "@hooks/useOnClickOutside";

const isAllSelectedInTab = (options, selection) => {
  if (options.length === 0) return true;
  if (selection === null) return true;
  return Array.isArray(selection) && selection.length === options.length;
};

const getEffectiveSelection = (options, selection) => {
  if (options.length === 0) return [];
  if (selection === null) return options;
  return Array.isArray(selection) ? selection : [];
};

const isOptionSelected = (option, options, selection) => {
  const effective = getEffectiveSelection(options, selection);
  return effective.some((item) => item.value === option.value);
};

const CheckboxVisual = ({ checked }) => {
  const { colors } = useTheme();

  return (
    <div
      className={`relative h-4 w-4 flex-shrink-0 rounded border transition-all duration-200 ${
        checked
          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
      }`}
    >
      {checked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

const OPTION_HIGHLIGHT_SHADOW = "0px -0.1px 4px 1px rgba(0, 0, 0, 0.6)";

const OptionRow = ({ checked, label, onToggle, className = "" }) => {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isHighlighted = checked || isHovered;
  const safeLabel = label ?? "";

  return (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onToggle}
      title={safeLabel}
      aria-label={safeLabel}
      style={
        isHighlighted
          ? {
              border: `1px solid ${colors.dropdownBorderColor}`,
              boxShadow: OPTION_HIGHLIGHT_SHADOW,
            }
          : { border: "1px solid transparent", boxShadow: "none" }
      }
      className={[
        "mb-[3px] flex w-full cursor-pointer items-center gap-1 rounded text-left text-sm transition-all duration-200",
        "px-2.5 py-1.5",
        colors.bgPrimary,
        isHighlighted ? colors.textActive : colors.textPrimary,
        className,
      ].join(" ")}
    >
      <CheckboxVisual checked={checked} />
      <span
        className="truncate overflow-hidden text-ellipsis whitespace-nowrap"
        title={safeLabel}
      >
        {safeLabel}
      </span>
    </button>
  );
};

const ProductTabPanel = ({
  options,
  selection,
  onSelectionChange,
  searchPlaceholder,
  selectAllLabel,
  emptyLabel,
  tabTestId,
}) => {
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const optionsListRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => option.label?.toLowerCase().includes(query));
  }, [options, search]);

  const allSelected = isAllSelectedInTab(options, selection);

  const preserveScrollPosition = (updateFn) => {
    const scrollTop = optionsListRef.current?.scrollTop ?? 0;
    updateFn();
    requestAnimationFrame(() => {
      if (optionsListRef.current) {
        optionsListRef.current.scrollTop = scrollTop;
      }
    });
  };

  const handleToggleSelectAll = () => {
    preserveScrollPosition(() => {
      if (allSelected) {
        onSelectionChange([]);
        return;
      }
      onSelectionChange(null);
    });
  };

  const handleToggleOption = (option) => {
    preserveScrollPosition(() => {
      const current = getEffectiveSelection(options, selection);
      const isSelected = current.some((item) => item.value === option.value);

      if (isSelected) {
        onSelectionChange(current.filter((item) => item.value !== option.value));
        return;
      }

      const next = [...current, option];
      onSelectionChange(next.length === options.length ? null : next);
    });
  };

  return (
    <div className="flex min-h-0 flex-col gap-2" data-testid={tabTestId}>
      <input
        type="text"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={searchPlaceholder}
        className={`w-full rounded-lg border px-2 py-1.5 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder}`}
        data-testid={tabTestId ? `${tabTestId}-search` : undefined}
      />

      {options.length === 0 ? (
        <p className={`py-2 text-sm ${colors.textSubTitle}`}>{emptyLabel}</p>
      ) : (
        <>
          <OptionRow
            checked={allSelected}
            label={selectAllLabel}
            onToggle={handleToggleSelectAll}
            className="font-medium"
          />

          <div className={`my-1 border-t ${colors.borderPrimary}`} />

          <div
            ref={optionsListRef}
            className={`max-h-72 min-h-0 overflow-y-auto overscroll-contain rounded-lg p-2 ${colors.bgPrimary}`}
            data-testid={tabTestId ? `${tabTestId}-options` : undefined}
            onWheel={(event) => event.stopPropagation()}
          >
            {filteredOptions.length === 0 ? (
              <p className={`py-2 text-sm ${colors.textSubTitle}`}>{emptyLabel}</p>
            ) : (
              filteredOptions.map((option) => (
                <OptionRow
                  key={option.value}
                  checked={isOptionSelected(option, options, selection)}
                  label={option.label}
                  onToggle={() => handleToggleOption(option)}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

const DashboardProductSelect = ({
  managedOptions,
  participateOptions,
  managedSelection,
  participateSelection,
  onManagedSelectionChange,
  onParticipateSelectionChange,
  placeholder,
  "data-testid": dataTestId,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const wrapperRef = useRef(null);

  useOnClickOutside(wrapperRef, () => setIsOpen(false), isOpen);

  const managedSelected = useMemo(
    () => getEffectiveSelection(managedOptions, managedSelection),
    [managedOptions, managedSelection],
  );
  const participateSelected = useMemo(
    () => getEffectiveSelection(participateOptions, participateSelection),
    [participateOptions, participateSelection],
  );

  const isAllProductsSelected = useMemo(() => {
    const totalCount = managedOptions.length + participateOptions.length;
    if (totalCount === 0) return false;
    return (
      isAllSelectedInTab(managedOptions, managedSelection) &&
      isAllSelectedInTab(participateOptions, participateSelection)
    );
  }, [managedOptions, participateOptions, managedSelection, participateSelection]);

  const displayLabel = useMemo(() => {
    if (isAllProductsSelected) {
      return t("dashboard.all-products");
    }

    return t("dashboard.product-filter-summary", {
      managedCount: managedSelected.length,
      participatedCount: participateSelected.length,
    });
  }, [isAllProductsSelected, managedSelected.length, participateSelected.length, t]);

  const tabLabels = useMemo(
    () => [
      { label: t("dashboard.product-filter-managed") },
      { label: t("dashboard.product-filter-participated") },
    ],
    [t],
  );

  const renderTabContent = (tabIndex) => {
    if (tabIndex === 0) {
      return (
        <ProductTabPanel
          options={managedOptions}
          selection={managedSelection}
          onSelectionChange={onManagedSelectionChange}
          searchPlaceholder={t("common.search-placeholder")}
          selectAllLabel={t("dashboard.all-products")}
          emptyLabel={t("dashboard.no-managed-products")}
          tabTestId={dataTestId ? `${dataTestId}-managed` : undefined}
        />
      );
    }

    return (
      <ProductTabPanel
        options={participateOptions}
        selection={participateSelection}
        onSelectionChange={onParticipateSelectionChange}
        searchPlaceholder={t("common.search-placeholder")}
        selectAllLabel={t("dashboard.all-products")}
        emptyLabel={t("dashboard.no-participated-products")}
        tabTestId={dataTestId ? `${dataTestId}-participated` : undefined}
      />
    );
  };

  const handleResetToAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onManagedSelectionChange(null);
    onParticipateSelectionChange(null);
  };

  const showClearButton =
    !isAllProductsSelected && managedOptions.length + participateOptions.length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid={dataTestId}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        className={`flex h-8 w-full cursor-pointer items-center gap-1 rounded-lg border px-2.5 text-left text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.borderHoverPrimary}`}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
      >
        <span className="min-w-0 flex-1 truncate">
          {managedOptions.length + participateOptions.length > 0 ? displayLabel : placeholder}
        </span>

        {showClearButton && (
          <button
            type="button"
            onClick={handleResetToAll}
            className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded ${colors.textSubTitle} ${colors.textHoverActive}`}
            aria-label={t("dashboard.all-products")}
            data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <svg
          className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div
          onMouseDown={(event) => event.stopPropagation()}
          className={`absolute right-0 z-50 mt-1 w-full overflow-hidden rounded p-0.5 shadow-[0px_0px_6px_1px_rgba(0,0,0,0.6)] ${colors.bgPrimary}`}
          data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
        >
          <div className="p-2">
            <TabsContainer
            compact
            activeTab={activeTab}
            onTabChange={setActiveTab}
            dataTestId={dataTestId ? `${dataTestId}-tabs` : undefined}
            tabs={tabLabels}
            renderTabContent={renderTabContent}
          />
          </div>
        </div>
      )}
    </div>
  );
};

export {
  DashboardProductSelect,
  getEffectiveSelection,
  isAllSelectedInTab,
};

export default DashboardProductSelect;
